import { forwardRef, useContext, useEffect, useMemo, useState } from 'react';

import { Button } from '@selina-finance/ui';
import clsx from 'clsx';
import mixpanel from 'mixpanel-browser';
import { useLocation, useNavigate } from 'react-router-dom';

import { ApplicationContext } from '@src/app/contexts/Application';
import { AuthContext } from '@src/app/contexts/Auth';
import { useLastFactFindField } from '@src/app/hooks/trackMixpanelEventsHooks/useLastFactFindField';
import { routesToSkip } from '@src/shared/lists/routes';
import { Avatar } from '@src/ui/components/Avatar';
import { Container } from '@src/ui/components/Container';
import { Dropdown, DropdownItem } from '@src/ui/components/Dropdown';
import { IconBox, LogoWithText } from '@src/ui/components/Icons';

import BackButton from '../BackButton/BackButton';
import { CreateAccountModal } from '../CreateAccountModal/CreateAccountModal';

const classes = {
    wrapper: 'flex px-4 py-5 items-center',
    wrapper_border: 'border-b border-disabled_contrast',
    container: 'flex justify-between',
};

export interface HeaderProps {
    className?: string;
}

const Header = forwardRef(({ className }: HeaderProps, ref: any) => {
    const { loggedIn } = useContext(AuthContext);
    const { applicationEmail } = useContext(ApplicationContext);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 1024);
        window.addEventListener('resize', handleResize);

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const [openCreateAccountModal, setOpenCreateAccountModal] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        if (openCreateAccountModal) {
            mixpanel.track('viewed_application_saved_fact_find');
        }
    }, [openCreateAccountModal]);

    const wrapperClassName = clsx(classes.wrapper);
    const userEmail = applicationEmail || '';
    const lastFactFindFieldRef = useLastFactFindField(location.pathname);
    const saveForLater = async () => {
        ref?.current.saveMethod();
    };

    const trackTappedSaveForLater = () => {
        mixpanel.track(
            'tapped_save_for_later_fact_find',
            lastFactFindFieldRef.current,
        );
    };

    const shouldShowButton = !routesToSkip.some((route) =>
        location.pathname.includes(route),
    );

    const shouldShowBackButton = useMemo(() => {
        const routesToShowBackButton = [
            '/eligibility/loan-details',
            '/eligibility/applicants',
            '/eligibility/property-details',
            '/eligibility/income',
            '/eligibility/outgoings',
            'offer/finalise',
            'offer/book-call',
            'debt-con/intro',
            'debt-con/main-goal',
            'debt-con/borrow',
            'debt-con/transition',
            'debt-con/input',
            'debt-con/new-input',
            'debt-con/review',
            'loan-preferences/questions',
            'loan-preferences/book-call',
            'loan-preferences/book-call/confirmation',
            'loan-preferences/book-call/calendar',
        ];
        return routesToShowBackButton.some((route) =>
            location.pathname.includes(route),
        );
    }, [location.pathname]);

    return (
        <Container
            variant={'default'}
            className={clsx([className, wrapperClassName])}
        >
            <Container variant={'wide'} className={classes.container}>
                {shouldShowBackButton && (
                    <div className='lg:hidden flex'>
                        <BackButton />
                    </div>
                )}
                <IconBox className='h-10'>
                    <LogoWithText />
                </IconBox>
                <div className={'flex gap-5 items-center'}>
                    {loggedIn && (
                        <>
                            <div
                                className={`hidden ${
                                    shouldShowBackButton
                                        ? 'lg:block'
                                        : 'sm:block'
                                }`}
                            >
                                {userEmail}
                            </div>
                            <Dropdown
                                alignment='right'
                                avatar={
                                    <Avatar size={'small'} name={userEmail} />
                                }
                            >
                                <DropdownItem
                                    onClick={() => navigate('/logout')}
                                >
                                    Log out
                                </DropdownItem>
                            </Dropdown>
                        </>
                    )}
                    {shouldShowButton && !loggedIn && (
                        <Button
                            color='outline-dark'
                            type='submit'
                            onClick={async () => {
                                trackTappedSaveForLater();
                                await saveForLater();
                                setOpenCreateAccountModal(true);
                            }}
                        >
                            {isMobile ? 'Save' : 'Save for later'}
                        </Button>
                    )}
                </div>
                <CreateAccountModal
                    open={openCreateAccountModal}
                    onClose={() => setOpenCreateAccountModal(false)}
                />
            </Container>
        </Container>
    );
});

export default Header;
